import axios from 'axios';

/**
 * Servicio para gestionar notificaciones push
 */
class PushNotificationService {
	constructor() {
		this.registration = null;
		this.subscription = null;
		this.isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
	}

	/**
	 * Inicializar el servicio de notificaciones push
	 */
	async initialize() {
		if (!this.isSupported) {
			console.warn('⚠️ Notificaciones push no soportadas en este navegador');
			return false;
		}

		try {
			// Obtener el registro del service worker
			const registration = await navigator.serviceWorker.ready;
			this.registration = registration;

			// Obtener la suscripción actual si existe
			this.subscription = await registration.pushManager.getSubscription();

			return true;
		} catch (error) {
			console.error('❌ Error al inicializar servicio de push:', error);
			return false;
		}
	}

	/**
	 * Solicitar permiso para notificaciones
	 */
	async requestPermission() {
		if (!this.isSupported) {
			return 'denied';
		}

		try {
			const permission = await Notification.requestPermission();
			return permission;
		} catch (error) {
			console.error('❌ Error al solicitar permiso:', error);
			return 'denied';
		}
	}

	/**
	 * Verificar si ya se tiene permiso
	 */
	getPermissionStatus() {
		if (!this.isSupported) {
			return 'denied';
		}
		return Notification.permission;
	}

	/**
	 * Suscribirse a notificaciones push
	 * @param {string} applicationServerKey - Clave pública del servidor (VAPID key)
	 */
	async subscribe(applicationServerKey) {
		if (!this.isSupported) {
			throw new Error('Notificaciones push no soportadas');
		}

		if (!this.registration) {
			await this.initialize();
		}

		// Verificar permiso
		if (Notification.permission === 'denied') {
			throw new Error('Permiso de notificaciones denegado');
		}

		// Solicitar permiso si no está concedido
		if (Notification.permission === 'default') {
			const permission = await this.requestPermission();
			if (permission !== 'granted') {
				throw new Error('Permiso de notificaciones no concedido');
			}
		}

		try {
			// Convertir la clave pública a formato Uint8Array
			const key = this.urlBase64ToUint8Array(applicationServerKey);

			// Suscribirse
			this.subscription = await this.registration.pushManager.subscribe({
				userVisibleOnly: true,
				applicationServerKey: key
			});

			return this.subscription;
		} catch (error) {
			console.error('❌ Error al suscribirse:', error);
			throw error;
		}
	}

	/**
	 * Desuscribirse de notificaciones push
	 */
	async unsubscribe() {
		if (!this.subscription) {
			return false;
		}

		try {
			const result = await this.subscription.unsubscribe();
			if (result) {
				this.subscription = null;
			}
			return result;
		} catch (error) {
			console.error('❌ Error al desuscribirse:', error);
			return false;
		}
	}

	/**
	 * Obtener la suscripción actual
	 */
	async getSubscription() {
		if (!this.registration) {
			await this.initialize();
		}

		if (!this.subscription) {
			this.subscription = await this.registration.pushManager.getSubscription();
		}

		return this.subscription;
	}

	/**
	 * Enviar la suscripción al servidor
	 * @param {string} url - URL del endpoint del servidor
	 * @param {object} subscription - Objeto de suscripción
	 */
	async sendSubscriptionToServer(url, subscription) {
		try {
			const response = await axios.post(url, {
				subscription: subscription.toJSON()
			});
			return response.data;
		} catch (error) {
			console.error('❌ Error al enviar suscripción al servidor:', error);
			throw error;
		}
	}

	/**
	 * Eliminar la suscripción del servidor
	 * @param {string} url - URL del endpoint del servidor
	 */
	async removeSubscriptionFromServer(url) {
		try {
			const subscription = await this.getSubscription();
			if (!subscription) {
				return true;
			}

			const response = await axios.post(url, {
				subscription: subscription.toJSON()
			});
			return response.data;
		} catch (error) {
			console.error('❌ Error al eliminar suscripción del servidor:', error);
			throw error;
		}
	}

	/**
	 * Convertir clave VAPID de base64 URL a Uint8Array
	 * @param {string} base64String - Clave en formato base64 URL
	 */
	urlBase64ToUint8Array(base64String) {
		const padding = '='.repeat((4 - base64String.length % 4) % 4);
		const base64 = (base64String + padding)
			.replace(/\-/g, '+')
			.replace(/_/g, '/');

		const rawData = window.atob(base64);
		const outputArray = new Uint8Array(rawData.length);

		for (let i = 0; i < rawData.length; ++i) {
			outputArray[i] = rawData.charCodeAt(i);
		}
		return outputArray;
	}

	/**
	 * Verificar si está suscrito
	 */
	async isSubscribed() {
		const subscription = await this.getSubscription();
		return subscription !== null;
	}
}

// Exportar instancia singleton
export default new PushNotificationService();




// WEBPACK FOOTER //
// ./src/services/push-notifications.js